import { FloatingLabel, Form, Spinner } from "react-bootstrap";

import { useFormik } from "formik";
import {
  showToastError,
  showToastSuccess,
} from "components/toastify/ToastNotifications";
import { AiOutlineClose } from "react-icons/ai";
import {
  useCreateConversionRateMutation,
  useGetSupportedCurrenciesQuery,
} from "api/ruleEngineApi";
import { useEffect, useState } from "react";
import { conversionRateSchema } from "validation-schema/rule-engine-schema";

type Props = {
  activeModalTab: string;
  onClose?: () => void;
  onSuccess?: () => void;
};

const AddConversionRateModal = ({
  activeModalTab,
  onClose,
  onSuccess,
}: Props) => {
  const [createConversionRate, { isLoading: isCreating }] =
    useCreateConversionRateMutation();

  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>([]);

  const {
    data: supportedCurrenciesData,
    error: currenciesError,
    isFetching: isFetchingCurrencies,
  } = useGetSupportedCurrenciesQuery();

  const formik = useFormik({
    initialValues: {
      application: "Joble",
      fromCurrency: "",
      toCurrency: "",
      exchangeRate: "",
      effectiveDate: "",
      expiryDate: "",
      source: "UI",
    },
    validationSchema: conversionRateSchema,
    onSubmit: async (values) => {
      
      
      

      try {
        const conversionRateData = {
          application: values.application,
          fromCurrency: values.fromCurrency,
          toCurrency: values.toCurrency,
          exchangeRate: parseFloat(values.exchangeRate) || 0,
          effectiveDate: values.effectiveDate
            ? new Date(values.effectiveDate).toISOString()
            : new Date().toISOString(),
          expiryDate: values.expiryDate
            ? new Date(values.expiryDate).toISOString()
            : new Date().toISOString(),
          source: values.source,
        };

        

        const result = await createConversionRate(conversionRateData).unwrap();

        

        showToastSuccess("Conversion Rate created successfully!");

        if (onSuccess) {
          onSuccess();
        } else if (onClose) {
          onClose();
        }
      } catch (error: any) {
        console.error("Error creating conversion rate:", error);
        showToastError(
          error?.data?.ResponseMessage ||
            error?.data?.message ||
            "An error occurred while creating the conversion rate"
        );
      }
    },
  });

  const incrementValue = (fieldName: string) => {
    const currentValue =
      parseFloat(
        formik.values[fieldName as keyof typeof formik.values] as string
      ) || 0;
    formik.setFieldValue(fieldName, (currentValue + 1).toString());
  };

  const decrementValue = (fieldName: string) => {
    const currentValue =
      parseFloat(
        formik.values[fieldName as keyof typeof formik.values] as string
      ) || 0;
    formik.setFieldValue(fieldName, Math.max(0, currentValue - 1).toString());
  };

  useEffect(() => {
    if (supportedCurrenciesData) {
      const response = supportedCurrenciesData;
      if (response.data?.items) {
        setSupportedCurrencies(response.data.items);
      }
    }
    if (currenciesError) {
      console.error("Error fetching supported currencies:", currenciesError);
      showToastError("Failed to load supported currencies");
    }
  }, [supportedCurrenciesData, currenciesError]);

  return (
    <div>
      {/* Header */}
      <div
        className="modal-header"
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "45px 24px 45px 24px",
          borderBottom: "none",
        }}
      >
        <h5
          style={{
            margin: 0,
            fontSize: "24px",
            fontWeight: "600",
            color: "#1f2937",
          }}
        >
          Add Conversion Rate
        </h5>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            fontWeight: "600",
            color: "#6b7280",
          }}
        >
          <AiOutlineClose />
        </button>
      </div>

      {/* Form Content */}
      <div style={{ padding: "0 24px 24px 24px" }}>
        <Form onSubmit={formik.handleSubmit}>
          {/* Row 1 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingFromCurrency"
                label="From Currency"
                className="float-input"
              >
                <Form.Select
                  name="fromCurrency"
                  value={formik.values.fromCurrency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Currency</option>
                  {supportedCurrencies?.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              {formik.touched.fromCurrency && formik.errors.fromCurrency && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.fromCurrency === "string"
                    ? formik.errors.fromCurrency
                    : "Invalid currency"}
                </span>
              )}
            </div>

            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingToCurrency"
                label="To Currency"
                className="float-input"
              >
                <Form.Select
                  name="toCurrency"
                  value={formik.values.toCurrency}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                >
                  <option value="">Select Currency</option>
                  {supportedCurrencies?.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </Form.Select>
              </FloatingLabel>
              {formik.touched.toCurrency && formik.errors.toCurrency && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.toCurrency === "string"
                    ? formik.errors.toCurrency
                    : "Invalid currency"}
                </span>
              )}
            </div>
          </div>

          {/* Row 2 */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingExchangeRate"
                label="Exchange Rate"
                className="float-input"
              >
                <Form.Control
                  type="number"
                  name="exchangeRate"
                  value={formik.values.exchangeRate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    border: "1px solid #d1d5db",
                    height: "58px",
                    paddingRight: "40px",
                    maxWidth: "246px",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => incrementValue("exchangeRate")}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "10px",
                      cursor: "pointer",
                      color: "#6b7280",
                      padding: "2px 4px",
                      lineHeight: "1",
                    }}
                  >
                    ▲
                  </button>
                  <button
                    type="button"
                    onClick={() => decrementValue("exchangeRate")}
                    style={{
                      background: "none",
                      border: "none",
                      fontSize: "10px",
                      cursor: "pointer",
                      color: "#6b7280",
                      padding: "2px 4px",
                      lineHeight: "1",
                    }}
                  >
                    ▼
                  </button>
                </div>
              </FloatingLabel>
              {formik.touched.exchangeRate && formik.errors.exchangeRate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.exchangeRate === "string"
                    ? formik.errors.exchangeRate
                    : "Invalid exchange rate"}
                </span>
              )}
            </div>

            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingEffectiveDate"
                label="Effective Date"
                className="float-input"
              >
                <Form.Control
                  type="date"
                  name="effectiveDate"
                  value={formik.values.effectiveDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Floating label select example"
                  className="border-0 w-100"
                />
              </FloatingLabel>
              {formik.touched.effectiveDate && formik.errors.effectiveDate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.effectiveDate === "string"
                    ? formik.errors.effectiveDate
                    : "Invalid effective date"}
                </span>
              )}
            </div>
          </div>

          {/* Row 3 - Added Expiry Date Field */}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-start",
              gap: "16px",
              marginBottom: "20px",
            }}
          >
            <div style={{ flex: 1, maxWidth: "246px" }}>
              <FloatingLabel
                controlId="floatingExpiryDate"
                label="Expiry Date"
                className="float-input"
              >
                <Form.Control
                  type="date"
                  name="expiryDate"
                  value={formik.values.expiryDate}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  style={{
                    borderRadius: "8px",
                    fontSize: "14px",
                    height: "58px",
                    maxWidth: "246px",
                  }}
                  aria-label="Expiry Date"
                  className="border-0 w-100"
                />
              </FloatingLabel>
              {formik.touched.expiryDate && formik.errors.expiryDate && (
                <span style={{ color: "#ef4444", fontSize: "12px" }}>
                  {typeof formik.errors.expiryDate === "string"
                    ? formik.errors.expiryDate
                    : "Invalid expiry date"}
                </span>
              )}
            </div>
          </div>

          <p
            style={{
              marginTop: "36px",
              marginBottom: "45px",
            }}
          >
            1 {formik.values.fromCurrency} = {formik.values.exchangeRate}{" "}
            {formik.values.toCurrency}
          </p>

          {/* Debug Info - Remove this after testing */}
          {/* <div
            style={{ marginBottom: "20px", fontSize: "12px", color: "#666" }}
          >
            <p>Form Valid: {formik.isValid ? "Yes" : "No"}</p>
            <p>Form Dirty: {formik.dirty ? "Yes" : "No"}</p>
            {Object.keys(formik.errors).length > 0 && (
              <p>Errors: {JSON.stringify(formik.errors)}</p>
            )}
          </div>  */}

          {/* Save Button */}
          <button
            disabled={isCreating}
            type="submit"
            style={{
              width: "100%",
              backgroundColor: "#0ea5e9",
              color: "white",
              border: "none",
              borderRadius: "24px",
              padding: "14px 20px",
              fontSize: "14px",
              fontWeight: "600",
              cursor: isCreating ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              opacity: isCreating ? 0.7 : 1,
              transition: "all 0.2s ease-in-out",
              borderTop: "",
            }}
            onMouseOver={(e) => {
              if (!isCreating) {
                e.currentTarget.style.backgroundColor = "#0284c7";
              }
            }}
            onMouseOut={(e) => {
              if (!isCreating) {
                e.currentTarget.style.backgroundColor = "#0ea5e9";
              }
            }}
          >
            Save {isCreating && <Spinner size="sm" />}
          </button>
        </Form>
      </div>
    </div>
  );
};

export default AddConversionRateModal;

